/**
 * @file task-groups.ts
 * @description Группы страницы «Задачи» по сроку (phase19.31, SC60):
 * «Просрочено», «Сегодня», «Завтра», «Позже», «Без срока» и — при
 * «Показать выполненные» — «Выполненные».
 *
 * День считается по поясу магазина (SC31), а не браузера: задача на 23:30
 * у сотрудника в другом поясе не должна «переехать» в «Завтра». Просрочено —
 * открытая задача, срок которой уже прошёл (в том числе сегодня утром).
 * Порядок строк внутри группы — серверный (по сроку, без срока — в конце).
 */

import type { ActivityDto } from "../../../lib/api-crm";

export type TaskGroupKey = "overdue" | "today" | "tomorrow" | "later" | "nodue" | "done";

/** Группы в порядке показа. */
export const TASK_GROUPS: readonly { key: TaskGroupKey; label: string }[] = [
  { key: "overdue", label: "Просрочено" },
  { key: "today", label: "Сегодня" },
  { key: "tomorrow", label: "Завтра" },
  { key: "later", label: "Позже" },
  { key: "nodue", label: "Без срока" },
  { key: "done", label: "Выполненные" },
];

type GroupInput = Pick<ActivityDto, "dueAt" | "completedAt">;

/** Календарный день «гггг-мм-дд» по поясу. */
export function dayKeyInZone(ms: number, timeZone: string): string {
  return new Date(ms).toLocaleDateString("en-CA", { timeZone });
}

/** Следующий день после «гггг-мм-дд» — арифметикой по дате, без часов. */
function nextDayKey(key: string): string {
  const date = new Date(`${key}T00:00:00Z`);
  date.setUTCDate(date.getUTCDate() + 1);
  return date.toISOString().slice(0, 10);
}

/** В какую группу попадает задача. */
export function taskGroupKey(task: GroupInput, now: number, timeZone: string): TaskGroupKey {
  if (task.completedAt) return "done";
  if (!task.dueAt) return "nodue";
  const due = Date.parse(task.dueAt);
  if (Number.isNaN(due)) return "nodue";
  if (due < now) return "overdue";
  const today = dayKeyInZone(now, timeZone);
  const day = dayKeyInZone(due, timeZone);
  if (day === today) return "today";
  if (day === nextDayKey(today)) return "tomorrow";
  return "later";
}

export interface TaskGroup<T> {
  key: TaskGroupKey;
  label: string;
  items: T[];
}

/** Разложить задачи по группам; пустые группы не возвращаются. */
export function groupTasks<T extends GroupInput>(items: readonly T[], now: number, timeZone: string): TaskGroup<T>[] {
  const buckets = new Map<TaskGroupKey, T[]>();
  for (const task of items) {
    const key = taskGroupKey(task, now, timeZone);
    const bucket = buckets.get(key);
    if (bucket) bucket.push(task);
    else buckets.set(key, [task]);
  }
  return TASK_GROUPS.flatMap((g) => {
    const bucket = buckets.get(g.key);
    return bucket ? [{ key: g.key, label: g.label, items: bucket }] : [];
  });
}
